import http from './http';
import { AxiosResponse } from 'axios';
import {
  ApiResponse,
  LoginRequest,
  RegisterRequest,
  AuthResponse,
  ApiUser,
} from '../types';
import {
  CreateFocusSessionDTO,
  UpdateFocusSessionDTO,
  FocusSession,
  PaginatedResponse,
  ApiFocusSession,
  mapApiFocusSessionToFocusSession,
  AnalyticsType,
  AnalyticsData,
  FocusStats,
  GameStats,
  GameAnalytics,
  CreateGameDTO,
  ChangePasswordRequest,
  Project,
  CreateProjectDTO,
  UpdateProjectDTO,
} from '../components/page-components/Dashboard/types';
import type {
  Invoice,
  Client,
  CreateInvoiceDTO,
  UpdateInvoiceDTO,
  CreateClientDTO,
  UpdateClientDTO,
} from '../components/free-tools/types';

/**
 * API utility functions
 * All API calls should go through these functions
 */

// Generic API response handler
const handleResponse = <T>(response: AxiosResponse<ApiResponse<T> | T>): T => {
  // Handle both ApiResponse format and direct data format
  if (
    typeof response.data === 'object' &&
    response.data !== null &&
    'success' in response.data
  ) {
    const apiResponse = response.data as ApiResponse<T>;
    if (apiResponse.success) {
      return apiResponse.data;
    }
    throw new Error(
      apiResponse.error || apiResponse.message || 'API request failed',
    );
  }
  // Direct data format
  return response.data as T;
};

// GET request
export const get = async <T = any>(url: string, config?: any): Promise<T> => {
  const response = await http.get<ApiResponse<T>>(url, config);
  return handleResponse<T>(response);
};

// POST request
export const post = async <T = any>(
  url: string,
  data?: any,
  config?: any,
): Promise<T> => {
  const response = await http.post<ApiResponse<T>>(url, data, config);
  return handleResponse<T>(response);
};

// PUT request
export const put = async <T = any>(
  url: string,
  data?: any,
  config?: any,
): Promise<T> => {
  const response = await http.put<ApiResponse<T>>(url, data, config);
  return handleResponse<T>(response);
};

// PATCH request
export const patch = async <T = any>(
  url: string,
  data?: any,
  config?: any,
): Promise<T> => {
  const response = await http.patch<ApiResponse<T>>(url, data, config);
  return handleResponse<T>(response);
};

// DELETE request
export const del = async <T = any>(url: string, config?: any): Promise<T> => {
  const response = await http.delete<ApiResponse<T>>(url, config);
  return handleResponse<T>(response);
};

// Export the http instance for direct use if needed
export { http };

// API endpoints
export const api = {
  // Auth endpoints
  auth: {
    // Register using email and password
    register: (data: RegisterRequest): Promise<AuthResponse> =>
      post<AuthResponse>('/auth/register', data),

    // Login using email and password
    login: (data: LoginRequest): Promise<AuthResponse> =>
      post<AuthResponse>('/auth/login', data),

    // Get logged in user info
    getMe: (): Promise<ApiUser> => get<ApiUser>('/auth/me'),

    // Refresh token with access
    // Accepts optional deviceId in the request body
    refresh: (deviceId?: string): Promise<AuthResponse> => {
      const body = deviceId ? { deviceId } : {};
      return post<AuthResponse>('/auth/refresh', body);
    },

    // Logout
    logout: () => post('/auth/logout'),

    // Google authentication - redirects to OAuth provider
    google: () => {
      const baseURL =
        import.meta.env.VITE_API_BASE_URL || 'http://localhost:3000/api';
      // Use current origin for callback URL (localhost when running locally, production URL when deployed)
      const currentOrigin = window.location.origin;
      const callbackUrl = `${currentOrigin}/callback`;
      window.location.href = `${baseURL}/auth/google?redirect_uri=${encodeURIComponent(callbackUrl)}`;
    },

    // Twitter authentication - redirects to OAuth provider
    twitter: () => {
      const baseURL =
        import.meta.env.VITE_API_BASE_URL || 'http://localhost:3000/api';
      // Use current origin for callback URL (localhost when running locally, production URL when deployed)
      const currentOrigin = window.location.origin;
      const callbackUrl = `${currentOrigin}/callback`;
      window.location.href = `${baseURL}/auth/twitter?redirect_uri=${encodeURIComponent(callbackUrl)}`;
    },

    // Change password
    changePassword: (data: ChangePasswordRequest): Promise<void> =>
      post<void>('/auth/change-password', data),

    // Forgot password
    forgotPassword: (email: string): Promise<void> =>
      post<void>('/auth/forgot-password', { email }),

    // Reset password
    resetPassword: (password: string, token: string): Promise<void> =>
      post<void>('/auth/reset-password', { password, token }),
  },

  // User endpoints
  user: {
    // Update user profile
    updateProfile: (data: Partial<ApiUser>): Promise<ApiUser> =>
      patch<ApiUser>('/user/profile', data),

    // Update user profile by ID
    updateUser: (id: string, data: any) => post(`/user/${id}`, data),

    // Delete user by ID
    deleteUser: (id: string) => del(`/user/${id}`),

    // Delete me
    deleteMe: () => del('/user/me'),
  },

  // Focus session endpoints
  focus: {
    // Create a new focus session
    create: async (data: CreateFocusSessionDTO): Promise<FocusSession> => {
      const apiSession = await post<ApiFocusSession>('/focus', data);
      return mapApiFocusSessionToFocusSession(apiSession);
    },

    // Get all focus sessions
    getAll: async (): Promise<FocusSession[]> => {
      const response =
        await http.get<PaginatedResponse<ApiFocusSession>>(
          '/focus/me/sessions',
        );
      // Handle both paginated response and direct array
      if ('data' in response.data && Array.isArray(response.data.data)) {
        return response.data.data.map(mapApiFocusSessionToFocusSession);
      }
      // Fallback: if it's already an array (for backward compatibility)
      if (Array.isArray(response.data)) {
        return (response.data as ApiFocusSession[]).map(
          mapApiFocusSessionToFocusSession,
        );
      }
      return [];
    },

    // Get a focus session by ID
    getById: async (id: string): Promise<FocusSession> => {
      const apiSession = await get<ApiFocusSession>(`/focus/${id}`);
      return mapApiFocusSessionToFocusSession(apiSession);
    },

    // Update a focus session
    update: async (
      id: string,
      data: UpdateFocusSessionDTO,
    ): Promise<FocusSession> => {
      // Convert Date objects to ISO strings for API
      const apiData = {
        ...data,
        ...(data.endTime && {
          endTime:
            data.endTime instanceof Date
              ? data.endTime.toISOString()
              : data.endTime,
        }),
      };
      const apiSession = await patch<ApiFocusSession>(`/focus/${id}`, apiData);
      return mapApiFocusSessionToFocusSession(apiSession);
    },

    // Delete a focus session
    delete: (id: string): Promise<void> => del<void>(`/focus/${id}`),

    // Get analytics data
    getAnalytics: async (
      type: AnalyticsType,
      projectId?: string,
    ): Promise<AnalyticsData> => {
      const params = projectId
        ? `type=${type}&projectId=${projectId}`
        : `type=${type}`;
      return get<AnalyticsData>(`/focus/me/analytics?${params}`);
    },

    // Get focus session stats
    getStats: async (
      type: AnalyticsType,
      projectId?: string,
    ): Promise<FocusStats> => {
      const params = projectId
        ? `?type=${type}&projectId=${projectId}`
        : `?type=${type}`;
      return get<FocusStats>(`/focus/me/stats${params}`);
    },

    // Get work log analytics
    getWorkLogAnalytics: async (
      period: 'week' | 'month' | 'year',
      projectId?: string,
    ): Promise<any> => {
      const params = new URLSearchParams({ period });
      if (projectId) params.append('projectId', projectId);
      return get<any>(`/focus/me/worklog?${params.toString()}`);
    },

    // Download work log
    downloadWorkLog: async (
      period: 'week' | 'month' | 'year',
      format: 'pdf' | 'csv' | 'excel',
      projectId?: string,
    ): Promise<Blob> => {
      const params = new URLSearchParams({ period, format });
      if (projectId) params.append('projectId', projectId);
      const response = await http.get(
        `/focus/me/worklog/download?${params.toString()}`,
        {
          responseType: 'blob',
        },
      );
      return response.data;
    },
  },

  // Projects endpoints
  projects: {
    // Get all projects for the current user (paginated response)
    getAll: async (): Promise<Project[]> => {
      const response = await http.get<
        ApiResponse<PaginatedResponse<Project>> | PaginatedResponse<Project>
      >('/project/me/projects');

      // Handle ApiResponse wrapper
      let responseData: PaginatedResponse<Project> | Project[];
      if (
        typeof response.data === 'object' &&
        response.data !== null &&
        'success' in response.data
      ) {
        const apiResponse = response.data as ApiResponse<
          PaginatedResponse<Project>
        >;
        responseData = apiResponse.success
          ? apiResponse.data
          : (response.data as unknown as PaginatedResponse<Project>);
      } else {
        responseData = response.data as PaginatedResponse<Project> | Project[];
      }

      // Handle paginated response format: {total, limit, offset, data: []}
      if ('data' in responseData && Array.isArray(responseData.data)) {
        return responseData.data;
      }
      // Fallback: if it's already an array (for backward compatibility)
      if (Array.isArray(responseData)) {
        return responseData as Project[];
      }
      return [];
    },

    // Get a project by ID
    getById: async (id: string): Promise<Project> => {
      return get<Project>(`/project/${id}`);
    },

    // Create a new project
    create: async (data: CreateProjectDTO): Promise<Project> => {
      const response = await http.post<
        ApiResponse<PaginatedResponse<Project>> | PaginatedResponse<Project>
      >('/project', data);

      // Handle ApiResponse wrapper
      let responseData: PaginatedResponse<Project> | Project;
      if (
        typeof response.data === 'object' &&
        response.data !== null &&
        'success' in response.data
      ) {
        const apiResponse = response.data as ApiResponse<
          PaginatedResponse<Project>
        >;
        responseData = apiResponse.success
          ? apiResponse.data
          : (response.data as unknown as PaginatedResponse<Project>);
      } else {
        responseData = response.data as PaginatedResponse<Project> | Project;
      }

      // Handle paginated response format: {total, limit, offset, data: []}
      if (
        'data' in responseData &&
        Array.isArray(responseData.data) &&
        responseData.data.length > 0
      ) {
        return responseData.data[0]; // Return the first (and only) project from the response
      }
      // Fallback: if it's a direct project object
      if ('id' in responseData) {
        return responseData as Project;
      }
      throw new Error('Invalid response format from create project endpoint');
    },

    // Update a project
    update: async (id: string, data: UpdateProjectDTO): Promise<Project> => {
      return patch<Project>(`/project/${id}`, data);
    },

    // Delete a project
    delete: (id: string): Promise<void> => {
      return del<void>(`/project/${id}`);
    },
  },

  // Games endpoints
  games: {
    // Get game stats
    getStats: async (): Promise<GameStats> => {
      // return get<GameStats>('/games/stats');
      return {} as any;
    },

    // Update game stats
    updateStats: async (stats: Partial<GameStats>): Promise<GameStats> => {
      // return put<GameStats>('/games/stats', stats);
      return {} as any;
    },

    // Create a game record
    createGame: async (gameData: CreateGameDTO): Promise<any> => {
      return post('/games', gameData);
    },

    // Get game analytics
    getAnalytics: async (): Promise<GameAnalytics> => {
      return get<GameAnalytics>('/games/me/analytics');
    },
  },

  // Invoices endpoints (Free Tools - sync when logged in)
  invoices: {
    getAll: () => get<Invoice[]>('/invoice/me'),
    create: (data: CreateInvoiceDTO) => post<Invoice>('/invoice', data),
    update: (id: string, data: UpdateInvoiceDTO) =>
      patch<Invoice>(`/invoice/${id}`, data),
    delete: (id: string) => del<void>(`/invoice/${id}`),
    bulkCreate: (data: CreateInvoiceDTO[]) =>
      post<Invoice[]>('/invoice/bulk', { invoices: data }),
  },

  // Clients endpoints (Free Tools - sync when logged in)
  clients: {
    getAll: () => get<Client[]>('/client/me'),
    create: (data: CreateClientDTO) => post<Client>('/client', data),
    update: (id: string, data: UpdateClientDTO) =>
      patch<Client>(`/client/${id}`, data),
    delete: (id: string) => del<void>(`/client/${id}`),
    bulkCreate: (data: CreateClientDTO[]) =>
      post<Client[]>('/client/bulk', { clients: data }),
  },

  // Feedbacks endpoints
  feedbacks: {
    // Submit feedback
    submitFeedback: (data: {
      name: string;
      email?: string;
      message: string;
    }): Promise<void> => post<void>('/feedbacks', data),
  },
};

export default api;
